import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FaVideo, FaMusic } from "react-icons/fa";
import { getMediaFileName } from "../utils/mediaUtils";
import { mediaShell } from "../../../styles/mediaShell";

function MediaThumbnailItem({ item, onClick, userName }) {
    const { media_type, url } = item;
    const name = getMediaFileName(item);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const imgRef = useRef(null);
    const videoRef = useRef(null);
    const observerRef = useRef(null);
    const prevUrlRef = useRef(null);

    useEffect(() => {
        if (!imgRef.current) return;

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        observerRef.current?.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1 }
        );

        observerRef.current.observe(imgRef.current);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        if (url !== prevUrlRef.current) {
            setIsLoaded(false);
            prevUrlRef.current = url;
        }
    }, [url]);

    const handleImageLoad = () => {
        setIsLoaded(true);
    };

    const handleImageError = () => {
        console.warn('Media thumbnail failed to load:', url);
    };

    const handleVideoLoadedMetadata = () => {
        // 動画のメタデータが読み込まれたら、最初のフレームを表示
        if (videoRef.current) {
            videoRef.current.currentTime = 0.1; // 最初のフレームを表示
            setIsLoaded(true);
        }
    };

    const handleVideoError = () => {
        console.warn('Video thumbnail failed to load:', url);
        setIsLoaded(true); // エラーでもロード状態にする（アイコンを表示）
    };

    return (
        <div
            className={`${mediaShell.thumbItem} group`}
            onClick={() => onClick(item)}
            ref={imgRef}
        >
            <div className={mediaShell.thumbBox}>
                {media_type === "image" ? (
                    <>
                        {isInView && (
                            <img
                                src={url}
                                alt={name}
                                className={mediaShell.thumbImage}
                                style={{
                                    opacity: isLoaded ? 1 : 0,
                                    transition: 'opacity 0.3s ease-in-out',
                                    imageOrientation: 'from-image'
                                }}
                                onLoad={handleImageLoad}
                                onError={handleImageError}
                                loading="lazy"
                            />
                        )}
                        {!isLoaded && isInView && (
                            <div className={mediaShell.thumbLoading}>
                                読み込み中...
                            </div>
                        )}
                    </>
                ) : media_type === "video" ? (
                    <>
                        {isInView && url && (
                            <video
                                ref={videoRef}
                                src={url}
                                className={mediaShell.thumbVideo}
                                style={{
                                    opacity: isLoaded ? 1 : 0,
                                    transition: 'opacity 0.3s ease-in-out'
                                }}
                                preload="metadata"
                                muted
                                playsInline
                                onLoadedMetadata={handleVideoLoadedMetadata}
                                onError={handleVideoError}
                            />
                        )}
                        {!isLoaded && isInView && (
                            <div className={mediaShell.thumbLoading}>
                                読み込み中...
                            </div>
                        )}
                        {!isLoaded && !isInView && (
                            <div className={mediaShell.thumbIconBox}>
                                <FaVideo />
                            </div>
                        )}
                    </>
                ) : (
                    <div className={mediaShell.thumbIconBox}>
                        <FaMusic />
                    </div>
                )}
            </div>
            <span className={mediaShell.thumbCaption}>{name}</span>
            {userName && (
                <span className={mediaShell.thumbUser}>{userName}</span>
            )}
        </div>
    );
}

MediaThumbnailItem.propTypes = {
    item: PropTypes.shape({
        media_type: PropTypes.string,
        url: PropTypes.string,
        name: PropTypes.string,
        filename: PropTypes.string,
    }).isRequired,
    onClick: PropTypes.func.isRequired,
    userName: PropTypes.string,
};

export default MediaThumbnailItem;
